import { action, computed, makeAutoObservable } from 'mobx';
import { cloneDeep } from 'lodash';
import {
    TopEventsEventsState,
    CompetitionCombinedType,
    TopEventsSportDataModelType,
} from 'src/AppState/CmsState/TopEventsState/TopEventsState';
import { arrayMove } from 'react-movable';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { SdkStaff } from 'src/AppState/sdk/staff';

export class TopEventsCompetitionsListState {
    public constructor(
        private readonly sdkStaff: SdkStaff,
        private readonly trpcClient: TrpcClient,
        private readonly topEventsEventsSectionState: TopEventsEventsState,
        private readonly sportData: TopEventsSportDataModelType
    ) {
        makeAutoObservable(this);
    }

    @computed public get isAnyCompetitions(): boolean {
        return this.sportData.competitions_list.some((competitions) =>
            competitions.events_list.some((event) => {
                const timeMatchStartedAndFinished = this.sdkStaff.models.getEvent(
                    event.event_id
                )?.timeMatchStartedAndFinished;

                return timeMatchStartedAndFinished === false;
            })
        );
    }

    @computed public get sportName(): string {
        return this.sdkStaff.models.getSport(this.sportData.sport_id)?.name ?? '';
    }

    private filterElements = (competition: CompetitionCombinedType, oldIndex: number, newIndex: number): boolean => {
        if (oldIndex > newIndex) {
            return competition.display_order >= newIndex && competition.display_order < oldIndex;
        } else {
            return competition.display_order <= newIndex && competition.display_order > oldIndex;
        }
    };

    private updateDisplayOrder = (
        arrayMoved: Array<CompetitionCombinedType>,
        oldIndex: number,
        newIndex: number
    ): Array<CompetitionCombinedType> => {
        const tempClonedArray = cloneDeep(arrayMoved);
        const topEventsCompetitionsToMove = arrayMoved
            .filter((comp) => this.filterElements(comp, oldIndex, newIndex))
            .map((c) => c.id);
        const shouldIncrementIndexes = oldIndex > newIndex;
        const draggedCompetition = arrayMoved.find((comp) => comp.display_order === oldIndex);

        for (let i = 0; i < tempClonedArray.length; i++) {
            const competition = tempClonedArray[i];
            if (competition === undefined) {
                return tempClonedArray;
            }
            if (topEventsCompetitionsToMove.includes(competition.id)) {
                if (shouldIncrementIndexes) {
                    competition.display_order = competition.display_order + 1;
                } else {
                    competition.display_order = competition.display_order - 1;
                }
            }

            if (competition.id === draggedCompetition?.id) {
                competition.display_order = newIndex;
            }
        }
        return tempClonedArray;
    };

    @action public handleCompetitionOrder = async ({
        oldIndex,
        newIndex,
    }: {
        oldIndex: number;
        newIndex: number;
    }): Promise<void> => {
        const tempCompetitionData = this.topEventsEventsSectionState.getCompetitionsArrayForDraggableList(
            this.sportData.sport_id
        );
        const displayOrderOfOldIndexElement = tempCompetitionData[oldIndex]?.display_order;
        const displayOrderOfNewIndexElement = tempCompetitionData[newIndex]?.display_order;
        const draggedCompetition = tempCompetitionData[oldIndex];

        if (displayOrderOfNewIndexElement === undefined || displayOrderOfOldIndexElement === undefined) {
            return;
        }

        const arrayMoved = arrayMove(tempCompetitionData, oldIndex, newIndex);
        const arrayWithUpdatedDisplayOrder = this.updateDisplayOrder(
            arrayMoved,
            displayOrderOfOldIndexElement,
            displayOrderOfNewIndexElement
        );
        const newOrder = this.topEventsEventsSectionState.setCompetitionsArrayForDraggableList(
            this.sportData.sport_id,
            arrayWithUpdatedDisplayOrder
        );
        this.topEventsEventsSectionState.setSportsData(newOrder);

        if (draggedCompetition === undefined) {
            return;
        }

        await this.trpcClient.client.topEventsCompetitions.changeTopEventsCompetitionsOrder.mutate({
            data: {
                id: draggedCompetition.id,
                new_order: displayOrderOfNewIndexElement,
                old_order: displayOrderOfOldIndexElement,
                sport_id: this.sportData.sport_id,
            },
        });

        await this.topEventsEventsSectionState.topEventsEventsResource.refresh();
    };
}
